import React, { useCallback } from 'react';
import { Container, Col, Row, Stack } from 'react-bootstrap';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { GREEN_YELLOW_RED_COLORS } from './candidate-scoring-util';

const CHART_TYPES = [
  {
    type: 'loyal_poor_performers',
    label1: 'High TRP',
    label2: 'Low LPP',
    chartColor1: GREEN_YELLOW_RED_COLORS.recommendWithCaution.primary,
    chartColor2: GREEN_YELLOW_RED_COLORS.recommendWithCaution.secondary,
  },
  {
    type: 'loyal_good_performers',
    label1: 'High TRP',
    label2: 'Moderate LPP',
    chartColor1: GREEN_YELLOW_RED_COLORS.recommend.primary,
    chartColor2: GREEN_YELLOW_RED_COLORS.recommend.secondary,
  },
  {
    type: 'high_overall_potential',
    label1: 'High TRP',
    label2: 'High LPP',
    chartColor1: GREEN_YELLOW_RED_COLORS.highlyRecommend.primary,
    chartColor2: GREEN_YELLOW_RED_COLORS.highlyRecommend.secondary,
  },
  {
    type: 'disengaged_poor_performers',
    label1: 'Moderate TRP',
    label2: 'Low LPP',
    chartColor1: GREEN_YELLOW_RED_COLORS.doNotRecommend.primary,
    chartColor2: GREEN_YELLOW_RED_COLORS.doNotRecommend.secondary,
  },
  {
    type: 'disengaged_good_performers',
    label1: 'Moderate TRP',
    label2: 'Moderate LPP',
    chartColor1: GREEN_YELLOW_RED_COLORS.recommendWithCaution.primary,
    chartColor2: GREEN_YELLOW_RED_COLORS.recommendWithCaution.secondary,
  },
  {
    type: 'disengaged_great_performers',
    label1: 'Moderate TRP',
    label2: 'High LPP',
    chartColor1: GREEN_YELLOW_RED_COLORS.recommend.primary,
    chartColor2: GREEN_YELLOW_RED_COLORS.recommend.secondary,
  },
  {
    type: 'apathetic_low_potential',
    label1: 'Low TRP',
    label2: 'Low LPP',
    chartColor1: GREEN_YELLOW_RED_COLORS.doNotRecommend.primary,
    chartColor2: GREEN_YELLOW_RED_COLORS.doNotRecommend.secondary,
  },
  {
    type: 'apathetic_good_potential',
    label1: 'Low TRP',
    label2: 'Moderate LPP',
    chartColor1: GREEN_YELLOW_RED_COLORS.doNotRecommend.primary,
    chartColor2: GREEN_YELLOW_RED_COLORS.doNotRecommend.secondary,
  },
  {
    type: 'apathetic_great_potential',
    label1: 'Low TRP',
    label2: 'High LPP',
    chartColor1: GREEN_YELLOW_RED_COLORS.recommendWithCaution.primary,
    chartColor2: GREEN_YELLOW_RED_COLORS.recommendWithCaution.secondary,
  },
];

const BaseDonut = ({ chartValue1, chartValue2, chartColor1, chartColor2, legendValue, labelValue, label1, label2 }) => (
  <Stack className="align-items-center">
    <Stack direction="horizontal" className="flex-row justify-content-start">
      <div className="pe-2 d-none d-xxl-block mt-2 mb-2" style={{ height: '8em', width: '8em' }}>
        <ResponsiveContainer height="100%" width="100%">
          <PieChart>
            <Pie
              data={[{ value: chartValue1 }, { value: chartValue2 }]}
              dataKey="value"
              innerRadius={25}
              outerRadius={40}
            >
              <Cell fill={chartColor1} />
              <Cell fill={chartColor2} />
            </Pie>
            <Legend content={<div>{`${legendValue}%`}</div>} verticalAlign="middle" />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="d-block d-xxl-none mt-4 mb-4" style={{ height: '4em', width: '4em' }}>
        <ResponsiveContainer height="100%" width="100%">
          <PieChart>
            <Pie
              data={[{ value: chartValue1 }, { value: chartValue2 }]}
              dataKey="value"
              innerRadius={15}
              outerRadius={25}
            >
              <Cell fill={chartColor1} />
              <Cell fill={chartColor2} />
            </Pie>
            <Legend content={<div>{`${legendValue}%`}</div>} verticalAlign="middle" />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="d-none d-xxl-block text-center nine-box-grid-item flex-fill">
        <div>{label1}</div>
        <div>{label2}</div>
        <div className="text-bolder text-larger">{`${labelValue}`}</div>
      </div>
      <div className="d-block d-xxl-none text-center nine-box-grid-item flex-fill text-small">
        <div>{label1}</div>
        <div>{label2}</div>
        <div className="text-bolder text-large">{`${labelValue}`}</div>
      </div>
    </Stack>
  </Stack>
);

const ClientDashboardLeadershipDonuts = ({ aggregations }) => {
  const chartTypes = useCallback(
    () =>
      CHART_TYPES.map((chart, index) => ({
        ...chart,
        chartValue1: aggregations.nineBoxValues.percentages[index],
        chartValue2: 100 - aggregations.nineBoxValues.percentages[index],
        legendValue: aggregations.nineBoxValues.percentages[index],
        labelValue: aggregations.nineBoxValues.counts[index],
      })),
    [aggregations]
  );

  return (
    <>
      {aggregations.nineBoxValues && (
        <>
          <div className="chart-title pb-2"> 9-Box Grid</div>
          <Stack direction="horizontal" className="flex-fill align-middle 9-box-grid">
            <Container className="m-auto">
              <Row>
                {aggregations &&
                  chartTypes().map(
                    ({
                      type,
                      chartValue1,
                      chartValue2,
                      chartColor1,
                      chartColor2,
                      legendValue,
                      labelValue,
                      label1,
                      label2,
                    }) => (
                      <Col key={type} xs={4}>
                        <BaseDonut
                          chartValue1={chartValue1}
                          chartValue2={chartValue2}
                          chartColor1={chartColor1}
                          chartColor2={chartColor2}
                          legendValue={legendValue}
                          labelValue={labelValue}
                          label1={label1}
                          label2={label2}
                        />
                      </Col>
                    )
                  )}
              </Row>
            </Container>
          </Stack>
        </>
      )}
    </>
  );
};

export default ClientDashboardLeadershipDonuts;
